import React, {
  useEffect,
  useRef,
  useState,
  useTransition,
  type ReactNode,
} from "react";
import { Button } from "../../button";
import { cn } from "../../utils";

export interface SettingsFormProps {
  /** Server action receiving the form's FormData on Save. */
  action: (formData: FormData) => Promise<void>;
  children: ReactNode;
  className?: string;
  /** Label on the primary button. Default "Save changes". */
  saveLabel?: string;
}

function serialize(form: HTMLFormElement | null): string {
  if (!form) return "";
  const params = new URLSearchParams();
  for (const [k, v] of new FormData(form).entries()) {
    if (typeof v === "string") params.append(k, v);
  }
  params.sort();
  return params.toString();
}

/**
 * A consolidated-settings form with a sticky "unsaved changes" bar.
 *
 * Wrap any number of plain, uncontrolled fields (`<input name>`,
 * `<select>`, `<textarea>` with `defaultValue`/`defaultChecked`). The form
 * snapshots its values on mount, watches for edits, and reveals a sticky
 * Save / Discard bar the moment anything diverges — so on a long scrolling
 * settings page every change has one obvious place to be saved. Save runs
 * the server action with the full FormData; Discard restores the snapshot.
 *
 * Immediate-action controls (toggles, row buttons that fire their own
 * server action on click) should live OUTSIDE this form — they save
 * themselves and don't participate in the dirty bar.
 */
export function SettingsForm({
  action,
  children,
  className,
  saveLabel = "Save changes",
}: SettingsFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const initial = useRef<string>("");
  const [dirty, setDirty] = useState(false);
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    initial.current = serialize(formRef.current);
    // Re-check once after mount in case defaults hydrated late.
    setDirty(false);
  }, []);

  function recompute() {
    setDirty(serialize(formRef.current) !== initial.current);
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = formRef.current;
    if (!form) return;
    const fd = new FormData(form);
    startTransition(async () => {
      await action(fd);
      initial.current = serialize(form);
      setDirty(false);
    });
  }

  function discard() {
    formRef.current?.reset();
    setDirty(false);
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      onInput={recompute}
      onChange={recompute}
      className={cn("space-y-6", className)}
    >
      {children}
      <div
        className={cn(
          "pointer-events-none sticky bottom-3 z-10 flex justify-center transition-opacity duration-200",
          dirty ? "opacity-100" : "hidden opacity-0",
        )}
        aria-hidden={!dirty}
      >
        <div className="pointer-events-auto flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 shadow-surface-3">
          <span className="pl-1 type-body text-muted-foreground">
            Unsaved changes
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={discard}
            disabled={pending}
          >
            Discard
          </Button>
          <Button type="submit" size="sm" disabled={pending}>
            {pending ? "Saving…" : saveLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}
